import React, { useState } from 'react';
import { User, Mail, Phone, Lock, MapPin, ArrowRight, ShieldCheck, CheckCircle2, LogOut, KeyRound } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { LocationSelector } from '../../components/common/LocationSelector';
import { JobMatchLogo } from '../../components/common/JobMatchLogo';
import { PhoneOtpVerificationStep } from '../../components/auth/PhoneOtpVerificationStep';
import { ProfilePhotoUploader } from '../../components/common/ProfilePhotoUploader';
import { GoogleSignInButton } from '../../components/auth/GoogleSignInButton';
import {
  GoogleAuthNoticeBanner,
  GoogleAccountLinkingCard,
  GooglePhoneVerificationCard,
} from '../../components/auth/GoogleAccountSecurityModals';
import { requestPhoneOtp, sendTwoFactorCodeApi, supabase } from '../../lib/supabaseService';

export const CustomerAuthPage: React.FC = () => {
  const {
    currentUser,
    registerCustomer,
    loginUser,
    logoutUser,
    navigateTo,
    viewParams,
    pendingGoogleAuth,
    setPendingGoogleAuth,
    initiateGoogleSignIn,
    selectRoleForGoogleUser,
  } = useApp();
  const [isLogin, setIsLogin] = useState(
    viewParams.authMode !== 'register' &&
      !viewParams.phone &&
      viewParams.ref !== 'telegram' &&
      pendingGoogleAuth?.status !== 'requires_onboarding'
  );
  const [authStep, setAuthStep] = useState<'form' | 'otp_verification' | 'login_2fa' | 'email_confirmation'>('form');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const isGoogleCustomerOnboarding =
    !isLogin &&
    Boolean(pendingGoogleAuth?.googleUser?.id) &&
    (pendingGoogleAuth?.status === 'requires_onboarding' ||
      pendingGoogleAuth?.status === 'requires_profile_completion');

  // Form State
  const [fullName, setFullName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(viewParams.phone || '+234 ');
  const [password, setPassword] = useState('');
  const [state, setState] = useState('Lagos');
  const [lga, setLga] = useState('Ikeja');
  const [cityArea, setCityArea] = useState('Ikeja GRA');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [challengeToken, setChallengeToken] = useState<string>('');
  const [registrationSessionId, setRegistrationSessionId] = useState<string>('');
  const [resumedActiveStage, setResumedActiveStage] = useState<'email' | 'phone'>('email');
  const [completingExistingUnverified, setCompletingExistingUnverified] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);

  // Restore incomplete customer registration draft on page refresh (excludes password)
  React.useEffect(() => {
    try {
      const savedDraft = sessionStorage.getItem('jobmatch_customer_reg_draft');
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        if (parsed && typeof parsed === 'object') {
          if (parsed.fullName) setFullName(parsed.fullName);
          if (parsed.email) setEmail(parsed.email);
          if (parsed.phone) setPhone(parsed.phone);
          if (parsed.state) setState(parsed.state);
          if (parsed.lga) setLga(parsed.lga);
          if (parsed.address) setAddress(parsed.address);
          if (parsed.registrationSessionId) {
            setRegistrationSessionId(parsed.registrationSessionId);
            fetch(
              `/api/auth/registration/session?sessionId=${encodeURIComponent(
                parsed.registrationSessionId
              )}&email=${encodeURIComponent(parsed.email || '')}&phone=${encodeURIComponent(parsed.phone || '')}`
            )
              .then((r) => r.json())
              .then((status) => {
                if (status?.found && status?.session && !status.session.consumed) {
                  setResumedActiveStage(status.session.emailVerified ? 'phone' : 'email');
                }
              })
              .catch(() => {});
          }
        }
      }
    } catch (_) {}
  }, []);

  // Persist non-sensitive customer registration draft in sessionStorage
  React.useEffect(() => {
    if (!isLogin) {
      try {
        sessionStorage.setItem(
          'jobmatch_customer_reg_draft',
          JSON.stringify({
            fullName,
            email,
            phone,
            state,
            lga,
            address,
            registrationSessionId,
          })
        );
      } catch (_) {}
    }
  }, [isLogin, fullName, email, phone, state, lga, address, registrationSessionId]);

  React.useEffect(() => {
    if (viewParams.authMode) {
      setIsLogin(viewParams.authMode !== 'register');
      setAuthStep('form');
      setError('');
      setSuccessMsg('');
    }
  }, [viewParams.authMode]);

  // Pre-fill customer registration form when returning from Google OAuth onboarding
  React.useEffect(() => {

    if (
      pendingGoogleAuth?.googleUser &&
      (pendingGoogleAuth.status === 'requires_onboarding' ||
        pendingGoogleAuth.status === 'requires_profile_completion')
    ) {
      setIsLogin(false);
      if (pendingGoogleAuth.googleUser.fullName && !fullName) {
        setFullName(pendingGoogleAuth.googleUser.fullName);
      }
      if (pendingGoogleAuth.googleUser.email) {
        setEmail(pendingGoogleAuth.googleUser.email);
      }
      if (pendingGoogleAuth.googleUser.avatarUrl && !avatarUrl) {
        setAvatarUrl(pendingGoogleAuth.googleUser.avatarUrl);
      }
    }
  }, [pendingGoogleAuth]);

  const handleGoogleClick = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await initiateGoogleSignIn({
        role: 'customer',
        authMode: isLogin ? 'login' : 'register',
        returnView: viewParams.returnView,
      });
    } catch (err: any) {
      setError(err?.message || 'Authentication failed. Could not start Google Sign-In.');
      setGoogleLoading(false);
    }
  };

  // If user is already authenticated as a customer
  if (currentUser && currentUser.role === 'customer') {
    return (
      <div className="max-w-md mx-auto my-8 sm:my-12 px-4">
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl text-center space-y-5">
          <div className="flex justify-center mb-2 cursor-pointer" onClick={() => navigateTo('home')}>
            <JobMatchLogo variant="compact" size="lg" />
          </div>
          <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-100">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-extrabold text-slate-900">Already Signed In</h2>
            <p className="text-xs text-slate-500">
              You are signed in as <strong>{currentUser.fullName}</strong> ({currentUser.email}).
            </p>
          </div>
          <div className="flex flex-col gap-2.5 pt-2">
            <button
              onClick={() => navigateTo(viewParams.returnView || 'customer_dashboard')}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Continue to Customer Dashboard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => logoutUser()}
              className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out & Switch Account</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If user is authenticated as a professional, refuse access to Customer login/dashboard portal
  if (currentUser && currentUser.role === 'professional') {
    return (
      <div className="max-w-md mx-auto my-8 sm:my-12 px-4">
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl text-center space-y-5">
          <div className="flex justify-center mb-2 cursor-pointer" onClick={() => navigateTo('home')}>
            <JobMatchLogo variant="compact" size="lg" />
          </div>
          <div className="w-14 h-14 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold border border-rose-100 shadow-xs">
            🚫
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-extrabold text-slate-900">Access Refused: Professional Account</h2>
            <p className="text-xs text-slate-600 leading-relaxed">
              You are currently signed in as a <strong>Professional</strong> (<strong>{currentUser.fullName}</strong> - {currentUser.email}). You cannot sign in to or access the Customer portal using a Professional account.
            </p>
            <p className="text-xs font-semibold text-slate-800 mt-2">
              Please go to your Professional Dashboard or switch to the Professional Login page.
            </p>
          </div>
          <div className="flex flex-col gap-2.5 pt-2">
            <button
              onClick={() => navigateTo('pro_dashboard')}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Go to Professional Dashboard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigateTo('pro_auth', { authMode: 'login' })}
              className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Go to Professional Login Page</span>
            </button>
            <button
              onClick={() => logoutUser()}
              className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out & Switch to Customer Account</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (isLogin) {
      if (!email.trim()) {
        setError('Please enter your email address.');
        return;
      }
      if (!password) {
        setError('Please enter your password.');
        return;
      }
      setLoading(true);
      try {
        const otpRes = await sendTwoFactorCodeApi({
          email: email.trim().toLowerCase(),
          password,
          userType: 'customer',
          purpose: 'login_2fa',
        });
        if (otpRes.success && (otpRes.requiresTwoFactor === false || otpRes.twoFactorVerified)) {
          await handleLogin2faVerified(otpRes);
        } else {
          setChallengeToken('');
          setError(otpRes.message || 'Invalid email or password');
        }
      } catch (err: any) {
        setChallengeToken('');
        setError(err?.message || 'Invalid email or password');
      } finally {
        setLoading(false);
      }
    } else {
      if (!fullName.trim() || !email.trim() || !phone.trim() || (!isGoogleCustomerOnboarding && !password.trim())) {
        setError(
          isGoogleCustomerOnboarding
            ? 'Please fill in your full name, Nigerian mobile number, and service address.'
            : 'Please fill in your name, email, password, and Nigerian phone number.'
        );
        return;
      }

      setLoading(true);
      try {
        if (isGoogleCustomerOnboarding && pendingGoogleAuth?.googleUser?.id) {
          const res = await fetch('/api/auth/registration/google-phone-otp/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: registrationSessionId || undefined,
              role: 'customer',
              fullName: fullName.trim(),
              email: pendingGoogleAuth.googleUser.email.trim().toLowerCase(),
              phone: phone.trim(),
              googleUserId: pendingGoogleAuth.googleUser.id,
            }),
          });
          const otpRes = await res.json();
          if (res.ok && otpRes.success) {
            if (otpRes.sessionId) {
              setRegistrationSessionId(otpRes.sessionId);
            }
            setResumedActiveStage('phone');
            setAuthStep('otp_verification');
          } else {
            setError(
              otpRes.message ||
                otpRes.error ||
                'Invalid Nigerian phone number. Please enter a valid number (e.g. 0803 123 4567) for SMS verification.'
            );
          }
        } else {
          const res = await fetch('/api/auth/registration/email-otp/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: registrationSessionId || undefined,
              role: 'customer',
              fullName: fullName.trim(),
              email: email.trim().toLowerCase(),
              phone: phone.trim(),
            }),
          });
          const otpRes = await res.json();
          if (res.ok && otpRes.success) {
            if (otpRes.sessionId) {
              setRegistrationSessionId(otpRes.sessionId);
            }
            setResumedActiveStage('email');
            setAuthStep('otp_verification');
          } else {
            setError(
              otpRes.message ||
                otpRes.error ||
                'Invalid contact details. Please enter a valid email and Nigerian phone number (e.g. 0803 123 4567).'
            );
          }
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to dispatch verification code. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLogin2faVerified = async (verifyResult?: any) => {
    setLoading(true);
    try {
      await loginUser(email, 'customer', password, verifyResult);
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password');
      setChallengeToken('');
      setAuthStep('form');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!email.trim()) {
      setError('Please enter your registered email address.');
      return;
    }

    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const isProdHost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'jobmatch.ng' || window.location.hostname === 'www.jobmatch.ng');
      const redirectUrl = isProdHost
        ? 'https://jobmatch.ng/reset-password'
        : `${window.location.origin}/reset-password`;

      let dispatched = false;
      try {
        const res = await fetch('/api/auth/send-password-reset-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanEmail,
            resetUrl: redirectUrl,
            recipientName: cleanEmail.split('@')[0],
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success && json?.dispatchedWithToken) {
          dispatched = true;
        }
      } catch (_) {}

      // Fallback or primary: Supabase official resetPasswordForEmail
      if (!dispatched) {
        await supabase.auth.resetPasswordForEmail(cleanEmail, {
          redirectTo: redirectUrl,
        }).catch(() => {});
      }

      setSuccessMsg('If an account exists with this email, a password reset link has been sent.');
    } catch (_err) {
      setSuccessMsg('If an account exists with this email, a password reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerified = async (verifyResult?: any) => {
    setLoading(true);
    try {
      if (completingExistingUnverified && isLogin) {
        const compRes = await fetch('/api/auth/complete-unverified-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: verifyResult?.registrationSessionId || registrationSessionId,
            verificationProofToken: verifyResult?.verificationProofToken,
            email: email.trim().toLowerCase(),
            phone: phone.trim(),
            role: 'customer',
          }),
        });
        const compData = await compRes.json().catch(() => ({}));
        if (!compRes.ok || !compData?.success) {
          throw new Error(compData?.message || compData?.error || 'Could not complete account verification.');
        }
        setCompletingExistingUnverified(false);
        const loginOtpRes = await sendTwoFactorCodeApi({
          email: email.trim().toLowerCase(),
          password,
          userType: 'customer',
          purpose: 'login_2fa',
        });
        if (loginOtpRes.success && loginOtpRes.requiresTwoFactor === false && loginOtpRes.twoFactorVerified) {
          await handleLogin2faVerified(loginOtpRes);
          return;
        } else if (loginOtpRes.success && loginOtpRes.challengeToken) {
          setChallengeToken(loginOtpRes.challengeToken);
          setAuthStep('login_2fa');
          return;
        }
        await loginUser(email, 'customer', password);
        return;
      }

      const regUser = await registerCustomer(
        {
          fullName,
          avatarUrl: avatarUrl || undefined,
          email,
          phone,
          state,
          lga,
          cityArea,
          address,
          emailVerified: true,
          phoneVerified: true,
          phoneVerifiedAt: new Date().toISOString(),
          onboardingStatus: 'completed',
          professionalApprovalStatus: 'not_applicable',
          registrationSessionId: verifyResult?.registrationSessionId || registrationSessionId,
          verificationProofToken: verifyResult?.verificationProofToken,
          googleUserId: isGoogleCustomerOnboarding ? pendingGoogleAuth?.googleUser?.id : undefined,
        },
        password || undefined
      );
      try {
        sessionStorage.removeItem('jobmatch_customer_reg_draft');
      } catch (_) {}
      if ((regUser as any)?.requiresEmailConfirmation) {
        setAuthStep('email_confirmation');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to finalize customer registration.');
      setAuthStep('form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto my-8 sm:my-12 px-4">
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl">
        {authStep === 'form' ? (
          <>
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex justify-center mb-4 cursor-pointer" onClick={() => navigateTo('home')}>
                <JobMatchLogo variant="compact" size="lg" />
              </div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
                {isForgotPassword
                  ? 'Forgot your password?'
                  : isLogin
                  ? 'Welcome Back, Customer'
                  : 'Create Your Customer Account'}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {isForgotPassword
                  ? "Enter the email address associated with your JobMatch NG account. We'll send you a secure link to reset your password."
                  : isLogin
                  ? 'Sign in to track your posted jobs, compare quotes & chat with professionals.'
                  : 'Join JobMatch NG to find verified Nigerian professionals in minutes.'}
              </p>
            </div>

            {!isForgotPassword && (
              <div className="flex rounded-xl bg-slate-100 p-1 mb-6 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(true);
                    setError('');
                    setSuccessMsg('');
                  }}
                  className={`flex-1 py-2 rounded-lg transition-all cursor-pointer ${
                    isLogin ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setError('');
                    setSuccessMsg('');
                  }}
                  className={`flex-1 py-2 rounded-lg transition-all cursor-pointer ${
                    !isLogin ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Register
                </button>
              </div>
            )}

            <GoogleAuthNoticeBanner />
            <GoogleAccountLinkingCard />
            {!isLogin && <GooglePhoneVerificationCard />}

            {!isForgotPassword && !isGoogleCustomerOnboarding && (
              <div className="mb-6 space-y-4">
                <GoogleSignInButton
                  onClick={handleGoogleClick}
                  loading={googleLoading}
                  disabled={loading}
                  label="Continue with Google"
                />
                <div className="relative flex items-center justify-center">
                  <div className="border-t border-slate-200 w-full" />
                  <span className="bg-white px-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                    or continue with email
                  </span>
                  <div className="border-t border-slate-200 w-full" />
                </div>
              </div>
            )}

            {isGoogleCustomerOnboarding && pendingGoogleAuth?.googleUser && (
              <div className="mb-5 p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-bold">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Google Email Verified ({pendingGoogleAuth.googleUser.email})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingGoogleAuth(null)}
                    className="text-[11px] text-slate-500 hover:text-slate-800 underline cursor-pointer"
                  >
                    Change
                  </button>
                </div>
                <p className="text-[11px] text-emerald-800 leading-relaxed">
                  Complete your Customer profile details below and verify your <strong>Nigerian mobile number via SMS OTP</strong> to activate your account.
                </p>
                <div className="pt-1 flex items-center justify-between border-t border-emerald-200/70 text-[11px]">
                  <span className="text-emerald-800 font-medium">Want to register as a Skilled Professional instead?</span>
                  <button
                    type="button"
                    onClick={() => selectRoleForGoogleUser('professional')}
                    className="font-extrabold text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                  >
                    Switch to Professional →
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="p-4 mb-4 rounded-2xl bg-rose-50 border border-rose-200 text-xs text-rose-800 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-base leading-none">🚫</span>
                  <div className="flex-1 font-semibold leading-relaxed">{error}</div>
                </div>
                {error.toLowerCase().includes('professional') && (
                  <button
                    type="button"
                    onClick={() => navigateTo('pro_auth', { authMode: 'login' })}
                    className="w-full mt-2 py-2 px-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>Go to Professional Login Page</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {successMsg && (
              <div className="p-3 mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                {successMsg}
              </div>
            )}

            {isForgotPassword ? (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4 text-xs">
                <div>
                  <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">Registered Email Address</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="email"
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer mt-4"
                >
                  {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setError('');
                    setSuccessMsg('');
                  }}
                  className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer mt-2"
                >
                  Back to Sign In
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 text-xs">
                {!isLogin && (
                  <div className="p-3 rounded-2xl bg-slate-900 text-white border border-slate-800 space-y-2 mb-3">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="font-extrabold uppercase tracking-wider text-emerald-400">
                        Sequential Customer Verification
                      </span>
                      <span className="text-slate-300 font-semibold">Stage 2 of 6: Details</span>
                    </div>
                    <div className="grid grid-cols-6 gap-1 text-[9px] font-bold text-center">
                      <div className="p-1.5 rounded-lg bg-emerald-950/90 border border-emerald-600/60 text-emerald-300 truncate">
                        1. Role ✓
                      </div>
                      <div className="p-1.5 rounded-lg bg-emerald-600 border border-emerald-400 text-white truncate">
                        2. Details
                      </div>
                      <div className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 truncate">
                        3. Email OTP
                      </div>
                      <div className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 truncate">
                        4. Phone SMS
                      </div>
                      <div className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 truncate">
                        5. Profile
                      </div>
                      <div className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 truncate">
                        6. Active
                      </div>
                    </div>
                  </div>
                )}

                {!isLogin && (
                  <div className="mb-4">
                    <ProfilePhotoUploader
                      photoUrl={avatarUrl}
                      onChange={setAvatarUrl}
                      userRole="customer"
                      label="Profile Photo (Optional)"
                    />
                  </div>
                )}

                {!isLogin && (
                  <div>
                    <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                      <input
                        type="text"
                        placeholder="e.g. Babatunde Williams"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">
                    Email Address{' '}
                    {isGoogleCustomerOnboarding && (
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold">(Verified by Google ✓)</span>
                    )}
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      readOnly={isGoogleCustomerOnboarding}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500 ${
                        isGoogleCustomerOnboarding ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </div>

                {!isLogin && (
                  <div>
                    <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Nigerian Mobile Number <span className="text-emerald-600 dark:text-emerald-400 font-normal">(SMS OTP Verified)</span>
                    </label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                      <input
                        type="tel"
                        placeholder="0803 123 4567 or +234 803 123 4567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500 font-mono"
                      />
                    </div>
                  </div>
                )}

                {!isGoogleCustomerOnboarding && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block font-semibold text-slate-700 dark:text-slate-200">Password</label>
                      {isLogin && (
                        <button
                          type="button"
                          onClick={() => {
                            navigateTo('password_reset', { email: email.trim(), role: 'customer' });
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-semibold cursor-pointer flex items-center gap-1"
                        >
                          <KeyRound className="w-3 h-3" />
                          <span>Forgot Password?</span>
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                      <input
                        type="password"
                        placeholder="••••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}

                {!isLogin && (
                  <>
                    <div className="pt-2 border-t border-slate-100">
                      <LocationSelector
                        selectedState={state}
                        selectedLga={lga}
                        onStateChange={setState}
                        onLgaChange={setLga}
                        idPrefix="cust-auth"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Estate / Street Address</label>
                      <div className="relative">
                        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                        <input
                          type="text"
                          placeholder="e.g. Block 4, Admiralty Way, Lekki Phase 1"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:border-emerald-500"
                        />
                      </div>
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer mt-6"
                >
                  {loading ? (
                    <span>{isLogin ? 'Authenticating...' : 'Sending Verification Code...'}</span>
                  ) : (
                    <>
                      <span>{isLogin ? 'Sign In' : 'Continue to Verification'}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </>
        ) : authStep === 'email_confirmation' ? (
          <div className="text-center space-y-6 py-4">
            <div className="flex justify-center cursor-pointer" onClick={() => navigateTo('home')}>
              <JobMatchLogo variant="compact" size="lg" />
            </div>
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto border border-emerald-100">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-extrabold text-slate-900">Check Your Email to Confirm Account</h2>
              <p className="text-xs text-slate-600">
                Your JobMatch NG customer account has been created in Supabase Authentication. A verification link has been sent to:
              </p>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900 text-sm">
                {email}
              </div>
              <p className="text-xs text-slate-500 pt-1">
                Please click the confirmation link in the email to activate your account. Once confirmed, you can sign in below.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsLogin(true);
                setAuthStep('form');
                setError('');
                setSuccessMsg('Account created! Please sign in with your email and password once confirmed.');
              }}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Proceed to Sign In</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : authStep === 'login_2fa' ? (
          <PhoneOtpVerificationStep
            email={email}
            userType="customer"
            purpose="login_2fa"
            challengeToken={challengeToken}
            onVerified={handleLogin2faVerified}
            onBack={() => {
              setChallengeToken('');
              setAuthStep('form');
            }}
          />
        ) : (
          <PhoneOtpVerificationStep
            phone={phone}
            email={email}
            fullName={fullName}
            userType="customer"
            purpose="registration"
            initialRegistrationSessionId={registrationSessionId}
            initialActiveStage={resumedActiveStage}
            onVerified={handleOtpVerified}
            onBack={() => setAuthStep('form')}
          />
        )}
      </div>
    </div>
  );
};

